import React from 'react';
import { Box, Typography, Fade, IconButton } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useVoiceNavigation } from './VoiceNavigationProvider';

// ============================================================================
// Voice Activation Overlay Component - Panel Design
// ============================================================================

// Animated orb component for the panel
const SiriOrb: React.FC<{ isListening: boolean; isProcessing: boolean }> = ({ isListening, isProcessing }) => {
  return (
    <Box
      sx={{
        position: 'relative',
        width: 60,
        height: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Outer glow */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(147,112,219,0.4) 0%, rgba(147,112,219,0) 70%)',
          animation: isListening ? 'siriPulse 1.5s ease-in-out infinite' : 'none',
          '@keyframes siriPulse': {
            '0%, 100%': { 
              transform: 'scale(1)',
              opacity: 0.6,
            },
            '50%': { 
              transform: 'scale(1.4)',
              opacity: 0.2,
            },
          },
        }}
      />
      
      {/* Middle ring */}
      <Box
        sx={{
          position: 'absolute',
          width: '85%',
          height: '85%',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(255,107,107,0.3) 0%, rgba(147,112,219,0.3) 50%, rgba(78,205,196,0.3) 100%)',
          animation: isListening ? 'siriRotate 3s linear infinite' : 'none',
          '@keyframes siriRotate': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' },
          },
        }}
      />
      
      {/* Core orb */}
      <Box
        sx={{
          position: 'relative',
          width: 45,
          height: 45,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #ff6b6b 0%, #9370db 50%, #4ecdc4 100%)',
          boxShadow: isListening 
            ? '0 0 30px rgba(147,112,219,0.6), 0 0 60px rgba(147,112,219,0.3), inset 0 0 20px rgba(255,255,255,0.3)'
            : '0 0 15px rgba(147,112,219,0.4), inset 0 0 15px rgba(255,255,255,0.2)',
          animation: isProcessing 
            ? 'siriThink 0.8s ease-in-out infinite' 
            : isListening 
              ? 'siriBreath 2s ease-in-out infinite' 
              : 'none',
          overflow: 'hidden',
          '@keyframes siriBreath': {
            '0%, 100%': { 
              transform: 'scale(1)',
              boxShadow: '0 0 30px rgba(147,112,219,0.6), 0 0 60px rgba(147,112,219,0.3), inset 0 0 20px rgba(255,255,255,0.3)',
            },
            '50%': { 
              transform: 'scale(1.08)',
              boxShadow: '0 0 40px rgba(147,112,219,0.8), 0 0 80px rgba(147,112,219,0.4), inset 0 0 25px rgba(255,255,255,0.4)',
            },
          },
          '@keyframes siriThink': {
            '0%, 100%': { transform: 'scale(1) rotate(0deg)' },
            '25%': { transform: 'scale(0.95) rotate(5deg)' },
            '50%': { transform: 'scale(1.05) rotate(0deg)' },
            '75%': { transform: 'scale(0.95) rotate(-5deg)' },
          },
        }}
      >
        {/* Shimmer effect */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '50%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
            transform: 'skewX(-20deg)',
            animation: isListening ? 'siriShimmer 2.5s ease-in-out infinite' : 'none',
            '@keyframes siriShimmer': {
              '0%': { left: '-100%' },
              '100%': { left: '200%' },
            },
          }}
        />
        {/* Inner highlight */}
        <Box
          sx={{
            position: 'absolute',
            top: '15%',
            left: '20%',
            width: '30%',
            height: '30%',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%)',
            filter: 'blur(2px)',
          }}
        />
        {/* Small sparkle */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            right: '20%',
            width: 4,
            height: 4,
            borderRadius: '50%',
            background: 'white',
            boxShadow: '0 0 4px 1px rgba(255,255,255,0.8)',
            opacity: isListening ? 0.9 : 0.5,
            transition: 'opacity 0.3s ease',
          }}
        />
      </Box>
      
      {/* Sound wave bars (visible when listening) */}
      {isListening && (
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            bottom: -15,
          }}
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <Box
              key={i}
              sx={{
                width: 3,
                height: 8,
                borderRadius: 1,
                backgroundColor: '#9370db',
                boxShadow: '0 0 6px rgba(147,112,219,0.5)',
                animation: 'siriWave 0.8s ease-in-out infinite',
                animationDelay: `${i * 0.1}s`,
                '@keyframes siriWave': {
                  '0%, 100%': { height: 4, opacity: 0.5 },
                  '50%': { height: 12, opacity: 1 },
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

interface VoiceActivationOverlayProps {
  /** Whether to show the overlay when voice navigation is active */
  showOnWakeWord?: boolean;
}

/**
 * Voice activation overlay component.
 * 
 * Displays a centered panel at the bottom of the screen showing the current
 * voice navigation state, including an animated orb, transcript, and status.
 * Supports keyboard navigation (Escape to close) and screen reader accessibility.
 * 
 * @param props - Component properties
 * @param props.showOnWakeWord - Show overlay during voice navigation (default: true)
 * 
 * @example
 * ```tsx
 * <VoiceNavigationProvider>
 *   <App />
 *   <VoiceActivationOverlay showOnWakeWord={true} />
 * </VoiceNavigationProvider>
 * ```
 */
const VoiceActivationOverlay: React.FC<VoiceActivationOverlayProps> = ({
  showOnWakeWord = true,
}) => {
  const { state, transcript, interimTranscript, matchedCommand, suggestions, cancelNavigation } = useVoiceNavigation();

  // Show overlay when listening for commands (after wake word), processing, or showing success/error
  const isVisible = showOnWakeWord && (
    state === 'listening' ||
    state === 'processing' ||
    state === 'confirming' ||
    state === 'success' ||
    state === 'error' ||
    state === 'not-found'
  );

  const isListening = state === 'listening';
  const isProcessing = state === 'processing' || state === 'confirming';
  const isSuccess = state === 'success';
  const isError = state === 'error' || state === 'not-found';

  // Get status text based on state
  const getStatusText = () => {
    switch (state) {
    case 'listening':
      return 'Listening...';
    case 'processing':
      return 'Processing...';
    case 'confirming':
      return matchedCommand ? `Going to ${matchedCommand.label}` : 'Processing...';
    case 'success':
      return matchedCommand ? matchedCommand.label : 'Done!';
    case 'error':
      return 'Try again';
    case 'not-found':
      return 'Didn\'t catch that';
    default:
      return '';
    }
  };

  // Get current transcript to display
  const displayTranscript = interimTranscript || transcript;

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <Fade in={isVisible} timeout={300}>
        <Box
          onClick={cancelNavigation}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(4px)',
            zIndex: 1399,
          }}
        />
      </Fade>

      {/* Modal container */}
      <Fade in={isVisible} timeout={300}>
        <Box
          role="dialog"
          aria-modal="true"
          aria-label="Voice navigation modal"
          aria-describedby="voice-nav-status"
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Escape') {
              cancelNavigation();
            }
          }}
          tabIndex={-1}
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1400,
            outline: 'none',
          }}
        >
          {/* Modal card */}
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              px: 6,
              py: 5,
              borderRadius: 4,
              background: isSuccess 
                ? 'linear-gradient(135deg, rgba(230, 245, 233, 0.98) 0%, rgba(237, 247, 237, 0.98) 100%)'
                : isError
                  ? 'linear-gradient(135deg, rgba(255, 235, 238, 0.98) 0%, rgba(255, 243, 245, 0.98) 100%)'
                  : 'linear-gradient(135deg, rgba(250, 245, 255, 0.98) 0%, rgba(245, 240, 255, 0.98) 100%)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 24px 64px rgba(147, 112, 219, 0.15), 0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.8)',
              minWidth: 380,
              maxWidth: 450,
              minHeight: 280,
            }}
          >
            {/* Close button */}
            <IconButton
              size="small"
              onClick={cancelNavigation}
              aria-label="Close voice navigation modal"
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                color: 'rgba(0, 0, 0, 0.4)',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  color: 'rgba(0, 0, 0, 0.6)',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>

            {/* Orb or Status Icon */}
            {isSuccess ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'successPop 0.5s ease-out',
                  '@keyframes successPop': {
                    '0%': { transform: 'scale(0.5)', opacity: 0 },
                    '50%': { transform: 'scale(1.1)' },
                    '100%': { transform: 'scale(1)', opacity: 1 },
                  },
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 90, color: '#4caf50' }} />
              </Box>
            ) : isError ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'errorShake 0.5s ease-out',
                  '@keyframes errorShake': {
                    '0%, 100%': { transform: 'translateX(0)' },
                    '25%': { transform: 'translateX(-10px)' },
                    '75%': { transform: 'translateX(10px)' },
                  },
                }}
              >
                <ErrorOutlineIcon sx={{ fontSize: 90, color: '#f44336' }} />
              </Box>
            ) : (
              <Box
                sx={{
                  transform: 'scale(1.6)',
                  mb: 2,
                }}
              >
                <SiriOrb isListening={isListening} isProcessing={isProcessing} />
              </Box>
            )}

            {/* Status Text */}
            <Typography
              id="voice-nav-status"
              variant="h5"
              role="status"
              aria-live="polite"
              sx={{
                fontWeight: 600,
                color: isSuccess ? '#2e7d32' : isError ? '#c62828' : '#2d1b4e',
                fontSize: '1.75rem',
                letterSpacing: '0.01em',
                textAlign: 'center',
                mt: isListening ? 3 : 0,
              }}
            >
              {getStatusText()}
            </Typography>

            {/* Display current transcript */}
            {displayTranscript && !isSuccess && !isError && (
              <Typography
                variant="body1"
                aria-live="polite"
                aria-label="Voice transcript"
                sx={{
                  color: '#5a4570',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  fontSize: '1.1rem',
                  mt: -1,
                  px: 2,
                  maxWidth: '100%',
                  wordWrap: 'break-word',
                }}
              >
                &ldquo;{displayTranscript}&rdquo;
              </Typography>
            )}

            {/* Show suggestions if no match */}
            {suggestions.length > 0 && state === 'not-found' && (
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  fontSize: '0.95rem',
                  textAlign: 'center',
                  mt: 1,
                  px: 2,
                }}
              >
                Try: {suggestions.slice(0, 2).map(s => s.label).join(', ')}
              </Typography>
            )}
          </Box>
        </Box>
      </Fade>
    </>
  );
};

export default VoiceActivationOverlay;
